/**
 * Paging arithmetic for the infinite-scroll lists.
 *
 * `vue-infinite-loading` has two terminal states: `loaded()`, which arms it for the
 * next scroll, and `complete()`, which makes it drop its scroll listener for good until
 * its `identifier` changes. Telling those apart correctly is the whole job here, and
 * getting it wrong is silent: the list simply stops paging.
 *
 * The lists this serves are fed by live Firestore listeners, so the number of available
 * items **grows while the user is looking at the list**. Completing because the
 * currently-known items happen to fit on one page is therefore wrong: the list mounts
 * about half a second after the screen opens, and for a master client with a thousand
 * devices arriving in chunks, that is exactly the moment when almost nothing has
 * arrived yet.
 */

export interface PageAdvanceInput {
  /** How many items are on screen now. */
  displayedCount: number;
  /** How many items to add per page. */
  pageSize: number;
  /** How many items are known right now, which can grow later. */
  availableCount: number;
}

export interface PageAdvanceResult {
  displayedCount: number;
  /**
   * True when everything currently known is on screen. The caller reports this to the
   * loader as `complete()`, and must re-arm when more items arrive - see
   * `shouldReArmPaging`.
   */
  hasDisplayedEverything: boolean;
}

/**
 * Grows the displayed slice by one page, never past what is available.
 */
export function advancePage({
  displayedCount,
  pageSize,
  availableCount,
}: PageAdvanceInput): PageAdvanceResult {
  const nextDisplayedCount = Math.min(displayedCount + pageSize, availableCount);

  return {
    displayedCount: nextDisplayedCount,
    hasDisplayedEverything: nextDisplayedCount >= availableCount,
  };
}

/**
 * Whether a loader that has completed needs to be armed again because more items have
 * since arrived. Without this, everything that loads after the loader ran out is
 * unreachable.
 */
export function shouldReArmPaging({
  hasDisplayedEverything,
  displayedCount,
  availableCount,
}: {
  hasDisplayedEverything: boolean;
  displayedCount: number;
  availableCount: number;
}) {
  return hasDisplayedEverything && availableCount > displayedCount;
}
